import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Row } from 'reactstrap';
import "../../../assets/css/gamesCss/Graph/mst.css"
import "../../../assets/css/GameField.css"
import { Col } from "reactstrap"
import GameOverModal from "../../../components/GameOverModal";
import GameWonModal from "../../../components/GameWonModal";
import { useDarkMode } from "../../../context/DarkModeProvider";
import { useLanguage } from 'context/LanguageProvider';


const calculateEdge = (from, to, nodes) => {

    const fromNode = nodes.find(n => n.id === from);
    const toNode = nodes.find(n => n.id === to);
    const dx = toNode.x - fromNode.x;
    const dy = toNode.y - fromNode.y;
    const angle = Math.atan2(dy, dx) * (180 / Math.PI);
    const nodeRadius = 30;

    const startX = fromNode.x + nodeRadius + (nodeRadius * Math.cos(angle * Math.PI / 180));
    const startY = fromNode.y + nodeRadius + (nodeRadius * Math.sin(angle * Math.PI / 180));
    const endX = toNode.x + nodeRadius - (nodeRadius * Math.cos(angle * Math.PI / 180));
    const endY = toNode.y + nodeRadius - (nodeRadius * Math.sin(angle * Math.PI / 180));

    return {
        startX,
        startY,
        endX,
        endY,
        angle,
        midX: (startX + endX) / 2,
        midY: (startY + endY) / 2
    };
};

const correctRoute = ["A", "D", "E", "F", "B", "C", "H", "I", "G", "J"];

// Arrow component με βάρη
const Arrow = React.memo(({ edge, index, isHovered, flowAnimation, nodes, onHover, onLeave }) => {
    const edgeData = calculateEdge(edge.from, edge.to, nodes);
    const strokeColor = edge.color;
    const strokeWidth = isHovered ? 5 : 4;

    return (
        <g>
            {/* Shadow line */}
            <motion.line
                x1={edgeData.startX + 2}
                y1={edgeData.startY + 2}
                x2={edgeData.endX + 2}
                y2={edgeData.endY + 2}
                stroke="rgba(0,0,0,0.3)"
                strokeWidth={strokeWidth + 2}
                strokeLinecap="round"
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: 1 }}
            />

            {/* Main line */}
            <motion.line
                x1={edgeData.startX}
                y1={edgeData.startY}
                x2={edgeData.endX}
                y2={edgeData.endY}
                stroke={strokeColor}
                strokeWidth={strokeWidth}
                strokeLinecap="round"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                onMouseEnter={onHover}
                onMouseLeave={onLeave}
                style={{ cursor: 'pointer' }}
            />

            {/* Weight label background */}
            <motion.text
                cx={edgeData.midX}
                cy={edgeData.midY - 5}
                r="12"
                fill="white"
                stroke={strokeColor}
                strokeWidth="2"
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.5 + index * 0.1, duration: 0.3 }}
                style={{
                    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
                }}
            />

            {/* Weight text */}
            <motion.text
                x={edgeData.midX - 15}
                y={edgeData.midY - 5}
                textAnchor="middle"
                fontSize="14"
                fontWeight="bold"
                fill={strokeColor}
                style={{
                    pointerEvents: 'none',
                    userSelect: 'none',
                    textShadow: '0 1px 2px rgba(255,255,255,0.8)'
                }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7 + index * 0.1, duration: 0.3 }}
            >
                {edge.weight}
            </motion.text>

            {/* Flow animation dots */}
            {(flowAnimation || isHovered) && [0, 1, 2].map((dotIndex) => (
                <motion.circle
                    key={`flow-${edge.from}-${edge.to}-${dotIndex}`}
                    r="4"
                    fill={strokeColor}
                    initial={{ cx: edgeData.startX, cy: edgeData.startY, opacity: 0 }}
                    animate={{ cx: edgeData.endX, cy: edgeData.endY, opacity: [0, 1, 1, 0] }}
                    transition={{
                        duration: 2.5,
                        delay: dotIndex * 0.4,
                        repeat: Infinity,
                        repeatDelay: 1,
                        ease: "easeInOut"
                    }}
                />
            ))}
        </g>
    );
});

const Node = React.memo(({ node, index, isSelected, onClick }) => (
    <motion.g>
        <motion.circle
            cx={node.x + 32}
            cy={node.y + 32}
            r="30"
            fill="rgba(0,0,0,0.25)"
        />
        <motion.circle
            cx={node.x + 30}
            cy={node.y + 30}
            r="30"
            fill={isSelected ? "#3317b2ff" : "#3b82f6"}
            stroke={"#1f2937"}
            strokeWidth={isSelected ? "4" : "3"}
            style={{ cursor: 'pointer' }}
            whileHover={{ scale: 1.15 }}
            onClick={onClick}
        />
        <motion.text
            x={node.x + 30}
            y={node.y + 37}
            textAnchor="middle"
            fontSize="20"
            fontWeight="bold"
            fill="white"
            style={{ pointerEvents: 'none', userSelect: 'none' }}
        >
            {node.id}
        </motion.text>
    </motion.g>
));

const GraphCanvas = React.memo(({ nodes, edges, selectedNodes, hoveredEdge, flowAnimation, onNodeClick, onEdgeHover, onEdgeLeave }) => (
    <svg
        className='mstSvg'
        width="100%"
        height="auto"
        viewBox="0 0 700 450"
    >
        <defs>
            <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#e2e8f0" strokeWidth="1" opacity="0.3" />
            </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid)" />
        {edges.map((edge, index) => (
            <Arrow
                key={`${edge.from}-${edge.to}`}
                edge={edge}
                index={index}
                isHovered={hoveredEdge?.from === edge.from && hoveredEdge?.to === edge.to}
                flowAnimation={flowAnimation}
                nodes={nodes}
                onHover={() => onEdgeHover(edge)}
                onLeave={() => onEdgeLeave()}
            />
        ))}
        {nodes.map((node, index) => (
            <Node
                key={node.id}
                node={node}
                index={index}
                isSelected={selectedNodes.includes(node.id)}
                onClick={() => onNodeClick(node.id)}
            />
        ))}
    </svg>
));

const SearchingDirected = () => {

    const { selLanguage, toggleLanguage, isLoading } = useLanguage();
    const mission = selLanguage ? "Εφάρμοσε τον αλγόριθμο του Prim για να δημιουργήσεις το Minimum spanning tree που προκύπτει από το παρακάτω γράφημα. Στόχος είναι να εςπιλέξεις τους κόμβους με την σωστή σειρά για να δημιουργηθεί το δέντρο. Με ένα click διαλέγεις έναν κόμβο ενώ με 2 τον βγάζεις από τις επιλογές σου. Μπορείς κάθε φορά να αφαιρέσεις μόνο τον τελευταίο κόμβο που βρίσκεται στην λίστα!" : "Apply Prim’s algorithm to create the minimum spanning tree derived from the following graph. Your goal is to select the nodes in the correct order to build the tree. With a single click you select a node, while with a double click you remove it from your choices. Each time, you can only remove the last node that is in the list!"
    const [showInstructions, setShowInstructions] = useState(true);

    const selectedStage = JSON.parse(localStorage.getItem("selectedStage"));
    var games = selectedStage.games;
    var nameGameKey = localStorage.getItem("gameClicked");
    var selectedGame = null;
    for (let i = 0; i < games.length; i++) {
        if (games[i].game_title === nameGameKey) {
            selectedGame = games[i];
            break;
        }
    }
    const handleRestart = () => {
        setSelectedNodes([]);
        setResult(null);
    }

    const resetGame = () => {
        setLifesLeft(3);
        setSelectedNodes([]);
        setResult(null);
        setShowGameOver(false);
        setShowGameWon(false);
        setHoveredEdge(null);
        setFlowAnimation(false);

        // Restart animations
        setTimeout(() => {
            setFlowAnimation(true);
        }, 4000);
    };

    const [lifesLeft, setLifesLeft] = useState(3);

    const nodes = useMemo(() => [
        { id: 'A', x: 100, y: 50 },
        { id: 'B', x: 300, y: 50 },
        { id: 'C', x: 500, y: 50 },
        { id: 'D', x: 50, y: 150 },
        { id: 'E', x: 200, y: 150 },
        { id: 'F', x: 350, y: 150 },
        { id: 'G', x: 500, y: 150 },
        { id: 'H', x: 150, y: 250 },
        { id: 'I', x: 350, y: 250 },
        { id: 'J', x: 250, y: 350 }
    ], []);

    const edges = useMemo(() => [
        { from: 'A', to: 'B', weight: 4, color: '#ef4444' },
        { from: 'A', to: 'D', weight: 2, color: '#3b82f6' },
        { from: 'B', to: 'C', weight: 3, color: '#10b981' },
        { from: 'B', to: 'E', weight: 5, color: '#f59e0b' },
        { from: 'B', to: 'F', weight: 1, color: '#8b5cf6' },
        { from: 'C', to: 'G', weight: 6, color: '#ef4444' },
        { from: 'D', to: 'E', weight: 3, color: '#06b6d4' },
        { from: 'E', to: 'F', weight: 2, color: '#10b981' },
        { from: 'E', to: 'H', weight: 4, color: '#f59e0b' },
        { from: 'F', to: 'G', weight: 5, color: '#8b5cf6' },
        { from: 'F', to: 'I', weight: 6, color: '#ef4444' },
        { from: 'G', to: 'I', weight: 2, color: '#06b6d4' },
        { from: 'H', to: 'I', weight: 1, color: '#10b981' },
        { from: 'H', to: 'J', weight: 4, color: '#f59e0b' },
        { from: 'I', to: 'J', weight: 3, color: '#8b5cf6' },
        { from: 'E', to: 'B', weight: 3, color: '#f97316' },
        { from: 'I', to: 'F', weight: 1, color: '#ec4899' }
    ], []);

    const [selectedNodes, setSelectedNodes] = useState([]);
    const [hoveredEdge, setHoveredEdge] = useState(null);
    const [flowAnimation, setFlowAnimation] = useState(false);
    const [result, setResult] = useState(null);
    const [showGameOver, setShowGameOver] = useState(false);
    const [showGameWon, setShowGameWon] = useState(false);
    const { darkMode } = useDarkMode();

    const handleNodeClick = useCallback((nodeId) => {
        setSelectedNodes(prev =>
            prev.includes(nodeId) ? prev.filter(id => id !== nodeId) : [...prev, nodeId]
        );
    }, []);

    useEffect(() => {
        const timeout = setTimeout(() => setFlowAnimation(true), 4000);
        return () => clearTimeout(timeout);
    }, []);

    // Check for Game Over and Game Won
    useEffect(() => {
        if (lifesLeft === 0) {
            setTimeout(() => setShowGameOver(true), 1000);
        }

        if (result?.success) {
            setTimeout(() => setShowGameWon(true), 1000);
        }
    }, [lifesLeft, result]);

    function checkRoute() {
        if (selectedNodes.length !== correctRoute.length) {
            setLifesLeft(prev => prev - 1);
            setResult({
                success: false,
                message: selLanguage
                    ? "Λάθος διαδρομή! Παρακαλώ προσπαθήστε ξανά."
                    : "Incorrect route! Please try again."
            });
            return;
        }

        for (var i = 0; i < selectedNodes.length; i++) {
            if (selectedNodes[i] !== correctRoute[i]) {
                setLifesLeft(prev => prev - 1);
                setResult({
                    success: false,
                    message: selLanguage
                        ? "Λάθος διαδρομή! Παρακαλώ προσπαθήστε ξανά."
                        : "Incorrect route! Please try again."
                });
                return;
            }
        }

        setResult({
            success: true,
            // Γραμμή ~363
            message: selLanguage
                ? "Συγχαρητήρια! Βρήκατε τη σωστή διαδρομή!"
                : "Congratulations! You found the correct route!"
        });
    }

    return (
        <div
            className='main-content mstContainer'
            style={{
                background: darkMode ? "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #101724ff 100%)" : "var(--lightModeGameBackground)"
            }}
        >
            <GameOverModal
                isVisible={showGameOver}
                onPlayAgain={resetGame}

            />

            <GameWonModal
                isVisible={showGameWon}
                onPlayAgain={resetGame}

            />
            <Col xl="12" lg="12" md="12" sm="12" xs="12">
                <div className="max-w-7xl mx-auto" style={{ width: "100%" }}>
                    {/* Lives Display */}
                    <div style={{ width: "100%", display: "flex", justifyContent: "flex-end", marginTop: "10px" }}>
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            flexDirection: "row",
                            justifyContent: "right",
                            gap: "12px"
                        }}>


                            <div style={{
                                display: "flex",
                                gap: "6px",
                                alignItems: "center"
                            }}>
                                {[1, 2, 3].map((heartNum) => (
                                    <div
                                        key={heartNum}
                                        style={{
                                            fontSize: "24px",
                                            filter: lifesLeft >= heartNum
                                                ? "drop-shadow(0 4px 8px rgba(220, 38, 38, 0.4))"
                                                : "grayscale(100%) opacity(0.3)",
                                            transform: lifesLeft >= heartNum ? "scale(1)" : "scale(0.8)",
                                            transition: "all 0.3s ease"
                                        }}
                                    >
                                        ❤️
                                    </div>
                                ))}
                            </div>

                        </div>
                    </div>

                    <br />

                    <div className='mstPron'>
                        {mission}
                    </div>

                    {/* Graph Frame */}
                    <div className='mstFrame'>
                        <GraphCanvas
                            nodes={nodes}
                            edges={edges}
                            selectedNodes={selectedNodes}
                            hoveredEdge={hoveredEdge}
                            flowAnimation={flowAnimation}
                            onNodeClick={handleNodeClick}
                            onEdgeHover={setHoveredEdge}
                            onEdgeLeave={() => setHoveredEdge(null)}
                        />
                    </div>

                    {result && (
                        <div
                            style={{
                                textAlign: "center",
                                padding: "20px",
                                borderRadius: "16px",
                                marginTop: "20px",
                                background: result.success
                                    ? "linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)"
                                    : "linear-gradient(135deg, #fef2f2 0%, #fecaca 100%)",
                                border: `2px solid ${result.success ? "#10b981" : "#ef4444"}`,
                                color: result.success ? "#059669" : "#dc2626",
                                fontWeight: 600,
                                fontSize: "1rem",
                                boxShadow: result.success
                                    ? "0 10px 25px rgba(16, 185, 129, 0.2)"
                                    : "0 10px 25px rgba(239, 68, 68, 0.2)"
                            }}
                        >
                            {result.message}
                        </div>
                    )}
                    <br></br>

                    {/* Submit Button */}
                    <div style={{ textAlign: "center", marginBottom: "20px" }}>
                        <Row style={{ display: "flex", justifyContent: "center", gap: "15px" }}>
                            <button
                                onClick={checkRoute}
                                className='commonGameCheckButton'
                                onMouseEnter={(e) => {
                                    e.target.style.transform = "scale(1.05)";
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.transform = "scale(1)";
                                }}
                            >
                                {selLanguage ? "Υποβολή Διαδρομής" : "Check solution"}
                            </button>

                            <button
                                className='commonGameDeleteButton'
                                onClick={() => {
                                    handleRestart();
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.transform = "scale(1.05)";

                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.transform = "scale(1)";

                                }}
                            >
                                {selLanguage ? "Καθαρισμός" : "Restart"}
                            </button>
                        </Row>
                    </div>
                </div>
            </Col>


        </div>
    );
};

export default SearchingDirected;